import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Plus, Trash2, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { Section, TextField } from './FormFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailIdentitiesTab = ({ role = 'admin' }) => {
  const [identities, setIdentities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [testMode, setTestMode] = useState(null);
  const [savingTestMode, setSavingTestMode] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const [previewingDigest, setPreviewingDigest] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestFormat, setDigestFormat] = useState('text');
  const [savingDigestFormat, setSavingDigestFormat] = useState(false);

  const fetchDigestFormat = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads/suppressed-digest/settings`, { withCredentials: true });
      setDigestFormat(res.data.format || 'text');
    } catch {
      // Silent — defaults to 'text'
    }
  };

  useEffect(() => { if (role === 'admin') fetchDigestFormat(); }, [role]);

  const fetchTestMode = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/email-identities/test-mode`, { withCredentials: true });
      setTestMode(res.data);
    } catch {
      // Silent — panel just stays in its loading state
    }
  };

  useEffect(() => { fetchTestMode(); }, []);

  const handleTestModeToggle = async (checked) => {
    setSavingTestMode(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/email-identities/test-mode`, { enabled: checked }, { withCredentials: true });
      setTestMode(res.data);
      toast.success(checked ? 'Safe Test Mode turned ON' : 'Safe Test Mode turned OFF — real emails will send');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update Safe Test Mode');
    } finally {
      setSavingTestMode(false);
    }
  };

  const handleClearOverride = async () => {
    setSavingTestMode(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/email-identities/test-mode`, { enabled: null }, { withCredentials: true });
      setTestMode(res.data);
      toast.success('Reverted to environment default');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset Safe Test Mode');
    } finally {
      setSavingTestMode(false);
    }
  };

  const handleSendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/leads/suppressed-digest/send-now`, {}, { withCredentials: true });
      setDigestResult(res.data);
      // `sent: false` can mean "no recipients" OR "recipients exist but the
      // real send failed" (SMTP error) — these are very different problems,
      // so the toast must check `recipients.length`, not just `sent`.
      if (res.data.sent) {
        toast.success(`Digest sent to ${res.data.recipients.length} admin(s)`);
      } else if (!res.data.recipients || res.data.recipients.length === 0) {
        toast.error('No recipients configured — add at least one Admin email first');
      } else {
        toast.error(`Failed to send to ${res.data.recipients.join(', ')} — check SMTP settings/logs`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const handlePreviewDigest = async () => {
    if (digestPreview) {
      setDigestPreview(null);
      return;
    }
    setPreviewingDigest(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads/suppressed-digest/preview`, { withCredentials: true });
      setDigestPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load digest preview');
    } finally {
      setPreviewingDigest(false);
    }
  };

  const handleDigestFormatChange = async (format) => {
    setSavingDigestFormat(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/leads/suppressed-digest/settings`, { format }, { withCredentials: true });
      setDigestFormat(res.data.format);
      setDigestPreview(null);
      toast.success(`Digest will now send as ${format === 'html' ? 'an HTML table' : 'plain text'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update digest format');
    } finally {
      setSavingDigestFormat(false);
    }
  };

  const fetchIdentities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/email-identities`, { withCredentials: true });
      setIdentities(res.data.identities || []);
    } catch {
      toast.error('Failed to load sender identities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdentities();
    axios.get(`${BACKEND_URL}/api/admin/leads/regions`, { withCredentials: true })
      .then((res) => setRegions((res.data.regions || []).filter((r) => r.code !== 'default')))
      .catch(() => {});
  }, []);

  const handleDefaultRegionChange = async (id, region) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/email-identities/${id}`, { default_region: region }, { withCredentials: true });
      toast.success(region ? 'Default region set' : 'Default region cleared');
      fetchIdentities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update default region');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !newLabel.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/email-identities`,
        { email: newEmail.trim().toLowerCase(), label: newLabel.trim() },
        { withCredentials: true });
      setNewEmail('');
      setNewLabel('');
      toast.success('Sender identity added');
      fetchIdentities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add identity');
    } finally {
      setSaving(false);
    }
  };

  const handleLabelChange = (id, label) => {
    setIdentities((prev) => prev.map((i) => (i.id === id ? { ...i, label } : i)));
  };

  const handleLabelBlur = async (id, label) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/email-identities/${id}`, { label }, { withCredentials: true });
      toast.success('Label saved');
    } catch {
      toast.error('Failed to save label');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this sender identity? It will disappear from the reply "From" dropdown.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/email-identities/${id}`, { withCredentials: true });
      setIdentities((prev) => prev.filter((i) => i.id !== id));
      toast.success('Sender identity removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove identity');
    }
  };

  const handleVerify = async (id) => {
    setVerifyingId(id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/email-identities/${id}/verify`, {}, { withCredentials: true });
      if (res.data.verified) {
        toast.success('Zoho accepted this alias — it can send successfully.');
      } else {
        toast.error(`Zoho rejected this alias: ${res.data.error || 'unknown error'}`);
      }
      fetchIdentities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <Section
        title="Safe Test Mode"
        description="When ON, outbound emails to fictional or undeliverable addresses are suppressed and logged instead of sent through Zoho — protects against rate limits during testing. Agents are always protected by this, regardless of this toggle."
      >
        <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="safe-test-mode-panel">
          <div>
            <p className="text-sm font-medium text-slate-800">
              Currently{' '}
              <span className={testMode?.effective ? 'text-emerald-600' : 'text-red-500'} data-testid="safe-test-mode-state">
                {testMode ? (testMode.effective ? 'ON' : 'OFF') : 'Loading…'}
              </span>
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {testMode && (testMode.override !== null && testMode.override !== undefined)
                ? `Admin override set by ${testMode.updated_by || 'admin'}`
                : `Using environment default (${testMode?.env_default ? 'ON' : 'OFF'})`}
            </p>
          </div>
          {role === 'admin' ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleTestModeToggle(!testMode?.effective)}
                disabled={savingTestMode || testMode === null}
                data-testid="safe-test-mode-toggle"
                className={`relative w-12 h-7 rounded-full transition-colors disabled:opacity-50 ${testMode?.effective ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${testMode?.effective ? 'left-6' : 'left-1'}`} />
              </button>
              {testMode && testMode.override !== null && testMode.override !== undefined && (
                <button
                  type="button"
                  onClick={handleClearOverride}
                  disabled={savingTestMode}
                  data-testid="safe-test-mode-clear-override"
                  className="text-xs text-slate-500 underline hover:text-slate-800"
                >
                  Reset to default
                </button>
              )}
            </div>
          ) : (
            <span className="text-xs text-slate-400 italic">Admin-only setting</span>
          )}
        </div>
        {role === 'admin' && (
          <div className="pt-3 border-t border-slate-100 mt-3 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="digest-format-toggle">
                <button
                  type="button"
                  onClick={() => handleDigestFormatChange('text')}
                  disabled={savingDigestFormat}
                  data-testid="digest-format-text"
                  className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${digestFormat === 'text' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
                >
                  Plain text
                </button>
                <button
                  type="button"
                  onClick={() => handleDigestFormatChange('html')}
                  disabled={savingDigestFormat}
                  data-testid="digest-format-html"
                  className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${digestFormat === 'html' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
                >
                  HTML table
                </button>
              </div>
              <button
                type="button"
                onClick={handlePreviewDigest}
                disabled={previewingDigest}
                data-testid="preview-digest-button"
                className="text-xs bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg font-medium hover:border-slate-400 disabled:opacity-50 transition-colors"
              >
                {previewingDigest ? 'Loading…' : digestPreview ? 'Hide preview' : 'Preview digest'}
              </button>
              <button
                type="button"
                onClick={handleSendDigestNow}
                disabled={sendingDigest}
                data-testid="send-digest-now-button"
                className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1.5 rounded-lg font-medium hover:bg-purple-100 disabled:opacity-50 transition-colors"
              >
                {sendingDigest ? 'Sending…' : 'Send suppression digest now'}
              </button>
              {digestResult && (
                <span className="text-xs text-slate-500" data-testid="digest-result-summary">
                  Last run: {digestResult.stats.total} suppressed in the last {digestResult.stats.hours}h
                </span>
              )}
            </div>
            {digestPreview && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs" data-testid="digest-preview-panel">
                <p className="font-semibold text-slate-700 mb-1">Subject: {digestPreview.subject}</p>
                {digestPreview.format === 'html' && digestPreview.html_body ? (
                  <div data-testid="digest-preview-html" dangerouslySetInnerHTML={{ __html: digestPreview.html_body }} />
                ) : (
                  <p className="text-slate-600 whitespace-pre-wrap">{digestPreview.body}</p>
                )}
                <p className="text-slate-400 mt-2">
                  Would send to: {digestPreview.recipients.length > 0 ? digestPreview.recipients.join(', ') : 'no Admin email configured'}
                </p>
              </div>
            )}
          </div>
        )}
      </Section>
      <Section title="Email Settings" description="Manage the Zoho 'Send Mail As' alias identities available when replying to leads. The visible sender name stays 'English Passion Team' for all aliases.">
      <div data-testid="email-identities-tab" className="space-y-6">
        <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex-1 min-w-[200px]">
            <TextField label="Alias email" value={newEmail} onChange={setNewEmail} placeholder="italy@englishpassion.uk" testId="new-identity-email-input" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <TextField label="Display label" value={newLabel} onChange={setNewLabel} placeholder="Italy Team" testId="new-identity-label-input" />
          </div>
          <button
            type="submit"
            disabled={saving || !newEmail.trim() || !newLabel.trim()}
            data-testid="add-identity-button"
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-slate-800 disabled:opacity-40 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>

        {loading ? (
          <p className="text-sm text-slate-400 italic">Loading sender identities…</p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden" data-testid="identities-list">
            {identities.map((idn) => (
              <div key={idn.id} className="flex items-center gap-3 p-3 bg-white" data-testid={`identity-row-${idn.id}`}>
                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  value={idn.label}
                  onChange={(e) => handleLabelChange(idn.id, e.target.value)}
                  onBlur={(e) => handleLabelBlur(idn.id, e.target.value)}
                  data-testid={`identity-label-input-${idn.id}`}
                  className="text-sm font-medium text-slate-800 border border-transparent hover:border-slate-200 focus:border-slate-900 rounded-md px-2 py-1 focus:outline-none w-48 flex-shrink-0"
                />
                <span className="text-sm text-slate-500 flex-1 truncate">{idn.email}</span>
                <select
                  value={idn.default_region || ''}
                  onChange={(e) => handleDefaultRegionChange(idn.id, e.target.value)}
                  data-testid={`identity-default-region-${idn.id}`}
                  title="Default region for outbound leads/auto-replies in this region"
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 flex-shrink-0 max-w-[140px]"
                >
                  <option value="">No default region</option>
                  {regions.map((r) => (
                    <option key={r.code} value={r.code}>{r.name}</option>
                  ))}
                </select>
                {idn.last_verify_ok === true && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`identity-verified-ok-${idn.id}`}>
                    <ShieldCheck className="w-3.5 h-3.5" /> Verified
                  </span>
                )}
                {idn.last_verify_ok === false && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-500" title={idn.last_verify_error} data-testid={`identity-verified-fail-${idn.id}`}>
                    <ShieldAlert className="w-3.5 h-3.5" /> Failed
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleVerify(idn.id)}
                  disabled={verifyingId === idn.id}
                  data-testid={`identity-verify-button-${idn.id}`}
                  className="text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-2.5 py-1.5 disabled:opacity-50"
                >
                  {verifyingId === idn.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(idn.id)}
                  data-testid={`identity-delete-button-${idn.id}`}
                  className="text-slate-400 hover:text-red-500 p-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {identities.length === 0 && (
              <p className="text-sm text-slate-400 italic p-4">No sender identities yet — add one above.</p>
            )}
          </div>
        )}
        <p className="text-xs text-slate-400">
          "Verify" sends a real test email from that alias to contact@englishpassion.uk — if Zoho hasn't been configured
          with this address as a "Send Mail As" identity yet, the send will fail and you'll see the reason here.
        </p>
      </div>
      </Section>
    </div>
  );
};

export default EmailIdentitiesTab;
